import { useApolloClient } from "@apollo/client";
import cuid from "cuid";
import yaml from "js-yaml";
import { get, isEmpty, isNil } from "lodash";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { createScreenDraft } from "utils/helpers/screenDraft";
import { draftErrorOf } from "./draft-errors";
import { resolveEmbeddedScreenWrite } from "./embeddedScreenWrite";
import { configToProcess, parseConfig } from "./importConfig";
import {
  remapImportedPlanIds,
  selectedPlanOptions,
} from "./remapImportedPlanIds";
import { DEFAULT_FIELDS_STRATEGY } from "./useProcessFieldsImport";
import { written } from "./useScreenWrites";

export const IMPORT_KINDS = {
  CANVAS: "canvas",
  FIELDS: "fields",
};

export const IMPORT_ERROR_CODES = {
  FIELDS_REQUIRE_EXISTING_PROCESS: "FIELDS_REQUIRE_EXISTING_PROCESS",
  INVALID_FILE: "INVALID_FILE",
};

const hasStatuses = (config) => {
  const statuses = config?.statuses || config?.processStatuses;
  return Array.isArray(statuses) && statuses.length > 0;
};

const hasFields = (config) => Array.isArray(config?.fields);

const exportedStatusIds = (config) =>
  (config?.statuses || config?.processStatuses || [])
    .map((status) => get(status, "id"))
    .filter((id) => !isEmpty(id));

/** Rebuild a fields-import YAML from a canvas (or fields-only) config object. */
export const fieldsContentFromConfig = (config) => {
  if (!hasFields(config)) return null;
  return yaml.dump(
    {
      process: config.process || config.name || "process",
      referentiels: config.referentiels || {},
      fields: config.fields,
      mappings: config.mappings || [],
    },
    { noRefs: true, sortKeys: false, lineWidth: 120 },
  );
};

export const useProcessImport = ({
  liveStepIds = [],
  onImportSteps = null,
} = {}) => {
  const client = useApolloClient();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const importedScreen = t("IMPORT_SUMMARY.IMPORTED_SCREEN");
  const draftErrors = t("CANVAS_EDITOR.DRAFT.ERRORS", { returnObjects: true });
  const [addProcess] = useMutation(graphql.mutations.CREATE_PROCESS);
  const [addScreen] = useMutation(graphql.mutations.ADD_SCREEN);
  const [addScreenVersion] = useMutation(graphql.mutations.ADD_SCREEN_VERSION);
  const [updateScreen] = useMutation(graphql.mutations.UPDATE_SCREEN);
  const [updateScreenVersion] = useMutation(
    graphql.mutations.UPDATE_SCREEN_VERSION,
    { onError: () => undefined },
  );
  const [updateScreenState] = useMutation(
    graphql.mutations.UPDATE_SCREEN_STATE,
    { onError: () => undefined },
  );
  const [addScreenState] = useMutation(graphql.mutations.ADD_SCREEN_STATE, {
    onError: () => undefined,
  });
  const [ensureScreenDraft] = useMutation(
    graphql.mutations.ENSURE_SCREEN_DRAFT,
    { onError: () => undefined },
  );
  const [validateFieldsImport] = useMutation(
    graphql.mutations.VALIDATE_PROCESS_FIELDS_IMPORT,
  );
  const [executeFieldsImport] = useMutation(
    graphql.mutations.EXECUTE_PROCESS_FIELDS_IMPORT,
  );

  const validateFields = useCallback(
    async (content, processId) => {
      const { data } = await validateFieldsImport({
        variables: {
          data: {
            process: { id: processId },
            content,
            strategy: DEFAULT_FIELDS_STRATEGY,
          },
        },
      });
      return data?.validateProcessFieldsImport || null;
    },
    [validateFieldsImport],
  );

  const applyFields = useCallback(
    async (content, processId) => {
      const { data } = await executeFieldsImport({
        variables: {
          data: {
            process: { id: processId },
            content,
            strategy: DEFAULT_FIELDS_STRATEGY,
          },
        },
      });
      return data?.executeProcessFieldsImport || null;
    },
    [executeFieldsImport],
  );

  const alignImportedPlans = useCallback(
    async (config) => {
      if (!selectedPlanOptions(config).length) return config;
      const { data } = await client.query({
        query: graphql.queries.INSURANCE_PLAN_CHOICES,
        variables: { where: {} },
        fetchPolicy: "network-only",
      });
      return remapImportedPlanIds(
        config,
        get(data, "insurancePlans.data") || [],
      );
    },
    [client],
  );

  const findTakenIds = useCallback(
    async (config, existingProcess) => {
      const ownId = get(existingProcess, "id");
      const statusIds = exportedStatusIds(config);
      const processIds = isNil(ownId) && !isEmpty(config.id) ? [config.id] : [];
      const [statuses, processes] = await Promise.all([
        statusIds.length
          ? client.query({
              query: graphql.queries.PROCESS_STATUS_OWNERS,
              variables: { isIn: { id: statusIds } },
              fetchPolicy: "network-only",
            })
          : null,
        processIds.length
          ? client.query({
              query: graphql.queries.PROCESS_IDS,
              variables: { isIn: { id: processIds } },
              fetchPolicy: "network-only",
            })
          : null,
      ]);
      return {
        statusIds: (get(statuses, "data.processStatuses.data") || [])
          .filter(
            (status) => isNil(ownId) || get(status, "process.id") !== ownId,
          )
          .map((status) => status.id),
        processIds: (get(processes, "data.processes.data") || []).map(
          (process) => process.id,
        ),
      };
    },
    [client],
  );

  const prepareImport = useCallback(
    async (file, { existingProcess } = {}) => {
      const text = await file.text();
      let config;
      try {
        config = parseConfig(text, file.name);
      } catch (_error) {
        throw new Error(IMPORT_ERROR_CODES.INVALID_FILE);
      }

      if (!config || typeof config !== "object") {
        throw new Error(IMPORT_ERROR_CODES.INVALID_FILE);
      }

      if (hasStatuses(config)) config = await alignImportedPlans(config);

      // Fields-only YAML: import FieldDefinitions onto the open process.
      if (hasFields(config) && !hasStatuses(config)) {
        if (!existingProcess?.id) {
          throw new Error(IMPORT_ERROR_CODES.FIELDS_REQUIRE_EXISTING_PROCESS);
        }
        const fieldsContent = text;
        const fieldsPreview = await validateFields(
          fieldsContent,
          existingProcess.id,
        );
        return {
          kind: IMPORT_KINDS.FIELDS,
          config,
          existingProcess,
          processInput: {
            id: existingProcess.id,
            name: existingProcess.name,
            type: existingProcess.type,
          },
          fieldsContent,
          fieldsPreview,
        };
      }

      const { processInput, embeddedScreens } = configToProcess(config, {
        existingProcess,
        taken: await findTakenIds(config, existingProcess),
      });

      let fieldsContent = null;
      let fieldsPreview = null;
      if (hasFields(config)) {
        fieldsContent = fieldsContentFromConfig(config);
        fieldsPreview = await validateFields(fieldsContent, processInput.id);
      }

      return {
        kind: IMPORT_KINDS.CANVAS,
        config,
        processInput,
        embeddedScreens,
        existingProcess,
        fieldsContent,
        fieldsPreview,
      };
    },
    [alignImportedPlans, findTakenIds, validateFields],
  );

  const createEmbeddedScreen = useCallback(
    async (screen, { statusId, screenId, versionNumber, draft }) =>
      createScreenDraft({
        addScreen,
        addScreenVersion,
        updateScreen,
        published: isNil(draft),
        statusId,
        screenId,
        processId: get(draft, "process.id") || null,
        draftOnly: !isNil(draft) && !liveStepIds.includes(statusId),
        description: screen.name || importedScreen,
        versionNumber,
        htmlContent: screen.htmlContent,
        styleContent: screen.styleContent || "",
        slots: screen.slots || [],
        states: screen.states || [],
        copyStates: true,
        translations: screen.translations || {},
      }),
    [addScreen, addScreenVersion, importedScreen, liveStepIds, updateScreen],
  );

  const screenDraftOf = useCallback(
    async (screenId) => {
      const result = await ensureScreenDraft({
        variables: { where: { id: screenId } },
      });
      const errors = get(result, "errors");
      if (!isNil(errors)) throw errors;
      return get(result, "data.ensureScreenDraft") || null;
    },
    [ensureScreenDraft],
  );

  const updateEmbeddedScreen = useCallback(
    async (screen, target) => {
      written(
        await updateScreenVersion({
          variables: {
            where: { id: target.versionId },
            data: {
              htmlContent: screen.htmlContent,
              styleContent: screen.styleContent || "",
              ...(target.bindingManifestId
                ? {
                    bindingManifest: {
                      id: target.bindingManifestId,
                      slots: screen.slots || [],
                    },
                  }
                : {}),
              ...(target.translationTableId
                ? {
                    translationTable: {
                      id: target.translationTableId,
                      entries: screen.translations || {},
                    },
                  }
                : {}),
            },
          },
        }),
      );

      for (const state of screen.states || []) {
        const live = (target.states || []).find(
          (item) => item.name === state.name,
        );
        if (get(live, "id")) {
          written(
            await updateScreenState({
              variables: {
                where: { id: live.id },
                data: {
                  htmlContent: state.htmlContent || "",
                  order: isNil(state.order) ? live.order : state.order,
                  conditions: state.conditions || live.conditions || [],
                  ...(get(live, "bindingManifest.id")
                    ? {
                        bindingManifest: {
                          id: live.bindingManifest.id,
                          slots:
                            state.slots ||
                            get(live, "bindingManifest.slots") ||
                            [],
                        },
                      }
                    : {}),
                },
              },
            }),
          );
        } else {
          written(
            await addScreenState({
              variables: {
                data: {
                  id: cuid(),
                  name: state.name,
                  order: state.order ?? 0,
                  htmlContent: state.htmlContent || "",
                  conditions: state.conditions || [],
                  screenVersion: { id: target.versionId },
                  bindingManifest: {
                    id: cuid(),
                    slots: state.slots || [],
                  },
                },
              },
            }),
          );
        }
      }

      return { screenId: target.screenId, versionId: target.versionId };
    },
    [addScreenState, updateScreenState, updateScreenVersion],
  );

  const writeEmbeddedScreen = useCallback(
    async (screen, target, draft) => {
      const version = isNil(target.screenId)
        ? null
        : await screenDraftOf(target.screenId);
      if (isNil(version))
        return createEmbeddedScreen(screen, {
          statusId: target.statusId,
          screenId: target.screenId,
          versionNumber: target.versionNumber,
          draft,
        });
      return updateEmbeddedScreen(screen, {
        screenId: target.screenId,
        versionId: version.id,
        bindingManifestId: get(version, "bindingManifest.id"),
        translationTableId: get(version, "translationTable.id"),
        states: version.states || [],
      });
    },
    [createEmbeddedScreen, screenDraftOf, updateEmbeddedScreen],
  );

  const applyEmbeddedScreens = useCallback(
    async (embeddedScreens, existingProcess) => {
      const results = [];
      const draft = isNil(existingProcess)
        ? null
        : { process: { id: existingProcess.id } };
      for (const screen of embeddedScreens || []) {
        const target = resolveEmbeddedScreenWrite(screen, existingProcess);
        try {
          const saved = await writeEmbeddedScreen(screen, target, draft);
          results.push({
            status: { id: target.statusId },
            screen: { id: saved.screenId },
          });
        } catch (error) {
          results.push({
            status: { id: target.statusId },
            error:
              get(draftErrors, draftErrorOf(error)) ||
              get(error, "message") ||
              String(error),
          });
        }
      }
      return results;
    },
    [draftErrors, writeEmbeddedScreen],
  );

  const runImport = useCallback(
    async ({
      kind,
      processInput,
      embeddedScreens,
      existingProcess,
      fieldsContent,
    }) => {
      let id = processInput?.id;
      let results = [];

      if (kind === IMPORT_KINDS.CANVAS) {
        if (existingProcess) onImportSteps(processInput.processStatuses);
        else await addProcess({ variables: { data: processInput } });

        id = processInput.id;

        const embeddedResults = await applyEmbeddedScreens(
          embeddedScreens,
          existingProcess,
        );
        results.push(...embeddedResults);
      }

      let fieldsResult = null;
      if (fieldsContent && id) {
        fieldsResult = await applyFields(fieldsContent, id);
        if (fieldsResult && !fieldsResult.success) {
          const detail = (fieldsResult.errors || [])
            .map((entry) => entry.key)
            .join(", ");
          throw new Error(detail || "FIELDS_IMPORT_FAILED");
        }
      }

      return { id, results, fieldsResult };
    },
    [addProcess, onImportSteps, applyFields, applyEmbeddedScreens],
  );

  return { prepareImport, runImport };
};
